import { forwardRef } from 'react'
import styled, { css } from 'styled-components'

import { spacing } from '../utils/spacing'

type InputProps = {
  id: string
  hasError?: boolean
} & React.ComponentProps<'input'>

const StyledInput = styled.input<InputProps>`
  -webkit-appearance: none;
  appearance: none;
  background: var(--color-inputBackground, var(--color-fillPrimary));
  color: var(--color-text);
  border: 1px solid var(--border-color);
  font-size: 16px;
  height: 56px;
  width: 100%;
  outline: none;
  padding: ${spacing[0.5]} ${spacing[1]};
  border-top-left-radius: var(--border-topLeftRadius, var(--border-radius));
  border-top-right-radius: var(--border-topRightRadius, var(--border-radius));
  border-bottom-left-radius: var(
    --border-bottomLeftRadius,
    var(--border-radius)
  );
  border-bottom-right-radius: var(
    --border-bottomRightRadius,
    var(--border-radius)
  );
  transition:
    box-shadow 150ms,
    border 150ms;
  ::placeholder {
    color: var(--color-text);
    opacity: 0.5;
  }
  :hover {
    border: 1px solid var(--color-accent);
    box-shadow: 0 0 12px
      color-mix(in srgb, var(--color-accent) 40%, transparent);
  }
  :focus {
    border: 1px solid var(--color-focus);
    box-shadow: 0 0 12px
      color-mix(in srgb, var(--color-focus) 40%, transparent);
  }
  :read-only,
  :disabled {
    background: var(--color-inputDisabled);
  }
  ${({ hasError }) =>
    hasError &&
    css`
      background: color-mix(in srgb, var(--color-error) 15%, transparent);
      border: 2px solid var(--color-error);
      :hover {
        border: 2px solid var(--color-accent);
        box-shadow: 0 0 12px
          color-mix(in srgb, var(--color-accent) 40%, transparent);
      }
      :focus {
        border: 2px solid var(--color-focus);
        box-shadow: 0 0 12px
          color-mix(in srgb, var(--color-focus) 40%, transparent);
      }
    `}
`

/**
 * Input component
 *
 * The `Input` component is a styled input element that supports various states such as error, read-only, and disabled.
 * It provides a consistent styling for input fields across the application.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.id - The unique identifier for the input element.
 * @param {boolean} [props.hasError] - If true, the input is styled to indicate an error state.
 * @param {string} [props.type='text'] - The type of the input element.
 * @param {boolean} [props.readOnly] - If true, the input is styled as read-only.
 * @param {React.Ref<HTMLInputElement>} ref - The ref to the input element.
 * @returns {JSX.Element} The rendered `Input` component.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ id, type = 'text', readOnly, hasError, ...props }, ref) => {
    return (
      <StyledInput
        ref={ref}
        id={id}
        type={type}
        hasError={hasError}
        {...props}
      />
    )
  }
)
